import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { PersonnelPickerDropdown } from './PersonnelPickerDropdown';
import { UserPickerDropdown } from './UserPickerDropdown';
import { usePageMeta } from '../hooks/usePageMeta';
import { useDistributorScope } from '../hooks/useDistributorScope';
import { formatTrPhone } from '../utils/phone';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/personnel';
const HIERARCHY_API = '/api/sales-org-hierarchy';

const WORK_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Aktif Çalışıyor' },
  { value: 'PASSIVE', label: 'Pasif / Ayrıldı' },
  { value: 'LEAVE', label: 'İzinde' },
];
const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const EMPTY_FORM = {
  sicilId: '', firstName: '', lastName: '', hireDate: '', workStatus: 'ACTIVE',
  position: '', phone: '', address: '', email: '', levelNo: 1, isActive: 'true',
  // #823 Distribütör — zorunlu değil; boş = tüm distribütörler için geçerli tanım.
  vendorId: '',
  // #44 Oturum hesabı — zorunlu değil; boş = personel sisteme giremez, mesajlaşamaz.
  userId: '',
};
const EMPTY_LEAVE = { startDate: '', endDate: '', requestDate: '', approvedBy: '', substitutePersonnelId: '' };

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }
function toDateInput(iso) { return iso ? String(iso).slice(0, 10) : ''; }
function fromDateInput(str) { return str ? new Date(str).toISOString() : null; }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString('tr-TR') : '—'; }

export function PersonnelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Personel' : 'Personel Detayı', 'Satış organizasyonu personel kartı ve izin bilgileri.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [levelNames, setLevelNames] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);
  const [personnelMap, setPersonnelMap] = useState(new Map());
  // #44 Bağlı kullanıcının görünen adı. Kaydın kendisi yalnız UserId taşır; etiketi
  // linkable-users'tan çözeriz (bağlı kullanıcı o listenin BAŞINDA döner).
  const [userLabel, setUserLabel] = useState('');

  // #823 Distribütör kapsamı — seçenekler kullanıcının yetkili olduğu distribütörler.
  const { vendorOptions, lockedOptions, isDistributorUser, lockedVendorId, scopeLoaded } = useDistributorScope();

  // İzin alt-grid
  const [leaves, setLeaves] = useState([]);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [leaveModal, setLeaveModal] = useState(null); // {id|null}
  const [leaveForm, setLeaveForm] = useState(EMPTY_LEAVE);
  const [leaveSaving, setLeaveSaving] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const levelOptions = useMemo(() => {
    const names = levelNames.length ? levelNames : ['Seviye 1', 'Seviye 2', 'Seviye 3'];
    return names.map((name, i) => ({ value: i + 1, label: name }));
  }, [levelNames]);

  // Seviye adları + isim haritası (vekalet eden etiketleri için)
  const fetchSupport = useCallback(async () => {
    try {
      const res = await fetch(HIERARCHY_API, { headers: authHeaders() });
      if (res.ok) setLevelNames(pick(unwrap(await res.json()), 'levelNames', 'LevelNames') ?? []);
    } catch { /* sessiz */ }
    try {
      const res = await fetch(`${API_BASE}?take=500`, { headers: authHeaders() });
      if (res.ok) {
        const items = unwrap(await res.json());
        const m = new Map();
        (Array.isArray(items) ? items : []).forEach((r) => {
          const rid = pick(r, 'id', 'Id');
          m.set(rid, `${pick(r, 'firstName', 'FirstName') ?? ''} ${pick(r, 'lastName', 'LastName') ?? ''} (${pick(r, 'sicilId', 'SicilId') ?? ''})`);
        });
        setPersonnelMap(m);
      }
    } catch { /* sessiz */ }
  }, []);

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Personel alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      setForm({
        sicilId: pick(r, 'sicilId', 'SicilId') ?? '',
        firstName: pick(r, 'firstName', 'FirstName') ?? '',
        lastName: pick(r, 'lastName', 'LastName') ?? '',
        hireDate: toDateInput(pick(r, 'hireDate', 'HireDate')),
        workStatus: pick(r, 'workStatus', 'WorkStatus') ?? 'ACTIVE',
        position: pick(r, 'position', 'Position') ?? '',
        phone: pick(r, 'phone', 'Phone') ?? '',
        address: pick(r, 'address', 'Address') ?? '',
        email: pick(r, 'email', 'Email') ?? '',
        levelNo: pick(r, 'levelNo', 'LevelNo') ?? 1,
        isActive: (pick(r, 'isActive', 'IsActive') ?? false) ? 'true' : 'false',
        vendorId: pick(r, 'vendorId', 'VendorId') ?? '',
        userId: pick(r, 'userId', 'UserId') ?? '',
      });
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Personel alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { fetchSupport(); }, [fetchSupport]);
  useEffect(() => { load(); }, [load]);

  // #44 Bağlı kullanıcının görünen adını çöz. Personel kaydı yalnız UserId taşır; etiket
  // linkable-users'tan gelir ve bağlı kullanıcı o listenin başında döndüğü için take sınırına
  // takılmaz. Çözülemezse seçici "Kullanıcı #id" gösterir — ekran çalışmaya devam eder.
  useEffect(() => {
    if (isNew || !form.userId || userLabel) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/linkable-users?personnelId=${id}&take=200`, { headers: authHeaders() });
        if (!res.ok) return;
        const items = unwrap(await res.json());
        const hit = (Array.isArray(items) ? items : [])
          .find((u) => String(pick(u, 'id', 'Id')) === String(form.userId));
        if (!cancelled && hit) setUserLabel(pick(hit, 'fullName', 'FullName') || '');
      } catch { /* sessiz — etiket kritik değil */ }
    })();
    return () => { cancelled = true; };
  }, [isNew, id, form.userId, userLabel]);

  // #823 Distribütör tipi kullanıcı: yetkili distribütör otomatik seçili + değiştirilemez.
  // load() bittikten sonra çalışır — aksi halde kaydın kendi vendorId'si oto-seçimi ezerdi.
  // Kayıtta başka bir distribütör yazıyorsa bile kullanıcı yalnız kendi distribütörüne
  // yazabildiği için form değeri kilitli değere sabitlenir.
  useEffect(() => {
    if (!scopeLoaded || loading || !lockedVendorId) return;
    setForm((c) => (String(c.vendorId ?? '') === lockedVendorId ? c : { ...c, vendorId: lockedVendorId }));
  }, [scopeLoaded, loading, lockedVendorId]);

  // Klonla: /personnel/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  // location.key her navigasyonda değiştiği için bir klon yalnızca bir kez uygulanır.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ── İzinler ──
  const fetchLeaves = useCallback(async (personnelId) => {
    if (!personnelId) { setLeaves([]); return; }
    setLeavesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${personnelId}/leaves`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`İzinler alınamadı (${res.status})`);
      const items = unwrap(await res.json());
      setLeaves((Array.isArray(items) ? items : []).map((l) => ({
        id: pick(l, 'id', 'Id'),
        startDate: pick(l, 'startDate', 'StartDate'),
        endDate: pick(l, 'endDate', 'EndDate'),
        requestDate: pick(l, 'requestDate', 'RequestDate'),
        approvedBy: pick(l, 'approvedBy', 'ApprovedBy') ?? '',
        substitutePersonnelId: pick(l, 'substitutePersonnelId', 'SubstitutePersonnelId') ?? '',
      })));
    } catch (err) {
      showToast('error', err.message || 'İzinler alınamadı');
      setLeaves([]);
    } finally {
      setLeavesLoading(false);
    }
  }, []);

  useEffect(() => { if (!isNew && activeTab === 'leaves') fetchLeaves(id); }, [isNew, activeTab, id, fetchLeaves]);

  const buildBody = () => ({
    sicilId: form.sicilId.trim(), firstName: form.firstName.trim(), lastName: form.lastName.trim(),
    hireDate: fromDateInput(form.hireDate), workStatus: form.workStatus,
    position: form.position, phone: form.phone, address: form.address, email: form.email,
    levelNo: Number(form.levelNo) || 1, isActive: form.isActive === 'true',
    // #823 Boş = tüm distribütörler için geçerli → backend'e null gitmeli ('' değil).
    vendorId: form.vendorId || null,
    // #44 Boş = oturum hesabı bağı yok → null ('' ya da 0 değil).
    userId: form.userId ? Number(form.userId) : null,
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan
  // (önceki personel backend'e kaydedilmiş olur). Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.sicilId.trim() || !form.firstName.trim() || !form.lastName.trim()) {
      showToast('error', 'Sicil ID, Ad ve Soyad zorunludur.'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(isNew ? API_BASE : `${API_BASE}/${id}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Kayıt başarısız (${res.status})`);
      if (isNew) {
        fetchSupport(); // isim haritası tazelensin (yeni personel vekalet seçeneklerinde çıksın)
        if (andNew) {
          showToast('success', `Personel eklendi. Yeni kayda devam edebilirsiniz.`);
          setForm(EMPTY_FORM);
          setUserLabel('');   // #44 bir kullanıcı bir personele bağlıdır; yeni kartta seçim sıfırlanır
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Personel eklendi.');
        setTimeout(() => navigate('/personnel'), 400);
        return;
      }
      showToast('success', 'Personel güncellendi.');
      setTimeout(() => navigate('/personnel'), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene kadar yeni personel oluşmaz.
  const handleClone = () => {
    navigate('/personnel/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Personel silindi.');
      setTimeout(() => navigate('/personnel'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  // ── İzin modal ──
  const openNewLeave = () => { setLeaveModal({ id: null }); setLeaveForm(EMPTY_LEAVE); };
  const openEditLeave = (l) => {
    setLeaveModal({ id: l.id });
    setLeaveForm({
      startDate: toDateInput(l.startDate), endDate: toDateInput(l.endDate),
      requestDate: toDateInput(l.requestDate), approvedBy: l.approvedBy || '',
      substitutePersonnelId: l.substitutePersonnelId || '',
    });
  };
  const setLeave = (k, v) => setLeaveForm((c) => ({ ...c, [k]: v }));

  const handleSaveLeave = async () => {
    if (isNew) return;
    if (!leaveForm.startDate || !leaveForm.endDate) { showToast('error', 'Başlangıç ve bitiş tarihi zorunlu.'); return; }
    setLeaveSaving(true);
    try {
      const payload = {
        startDate: fromDateInput(leaveForm.startDate), endDate: fromDateInput(leaveForm.endDate),
        requestDate: fromDateInput(leaveForm.requestDate), approvedBy: leaveForm.approvedBy,
        substitutePersonnelId: leaveForm.substitutePersonnelId || null,
      };
      const isNewLeave = !leaveModal.id;
      const url = isNewLeave ? `${API_BASE}/${id}/leaves` : `${API_BASE}/${id}/leaves/${leaveModal.id}`;
      const res = await fetch(url, { method: isNewLeave ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(payload) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `İzin kaydı başarısız (${res.status})`);
      showToast('success', isNewLeave ? 'İzin eklendi.' : 'İzin güncellendi.');
      setLeaveModal(null);
      fetchLeaves(id);
    } catch (err) {
      showToast('error', err.message || 'İzin kaydı başarısız');
    } finally {
      setLeaveSaving(false);
    }
  };

  const handleDeleteLeave = async (leaveId) => {
    try {
      const res = await fetch(`${API_BASE}/${id}/leaves/${leaveId}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error(`Silme başarısız (${res.status})`);
      showToast('success', 'İzin kaydı silindi.');
      fetchLeaves(id);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    }
  };

  const title = isNew ? 'Yeni Personel' : (`${form.firstName} ${form.lastName}`.trim() || 'Personel Detayı');
  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="neutral">Pasif</StatusBadge>);

  const tabs = [
    { key: 'general', label: 'Genel', icon: 'user' },
    { key: 'leaves', label: 'İzin Bilgileri', icon: 'calendar', disabled: isNew, title: isNew ? 'Önce personeli kaydedin' : undefined },
  ];

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/personnel')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni personel aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Personeller', to: '/personnel' }, { label: isNew ? 'Yeni Personel' : 'Personel Detayı', current: true }]}
      status={statusBadge}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Personnel', entityId: id }}
      loading={loading}
    >
      {activeTab === 'general' && (
        <div className="dps-form">
          <Section icon="user" title="Personel Bilgileri" subtitle="Kimlik, kademe ve çalışma durumu">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="sicilId" label="Sicil ID" req mono />
              <FieldSelect form={form} set={set} k="levelNo" label="Seviye" options={levelOptions} />
              <FieldText form={form} set={set} tried={tried} k="firstName" label="Ad" req />
              <FieldText form={form} set={set} tried={tried} k="lastName" label="Soyad" req />
              <FieldDate form={form} set={set} k="hireDate" label="İşe Giriş Tarihi" />
              <FieldSelect form={form} set={set} k="workStatus" label="Çalışma Durumu" options={WORK_STATUS_OPTIONS} />
              <FieldText form={form} set={set} k="position" label="Pozisyon" />
              <FieldSelect form={form} set={set} k="isActive" label="Aktiflik" options={ACTIVE_OPTIONS} />
              {/* #823 Distribütör — zorunlu değil. Boş bırakılırsa personel tüm distribütörler
                  için geçerlidir; seçilirse yalnız seçili distribütör için. Distribütör tipi
                  kullanıcıda yetkili distribütör otomatik seçili ve değiştirilemez. */}
              <FieldSelect
                form={form} set={set} k="vendorId" label="Distribütör"
                options={isDistributorUser && lockedVendorId ? lockedOptions : vendorOptions}
                disabled={Boolean(isDistributorUser && lockedVendorId)}
                hint={isDistributorUser && lockedVendorId
                  ? 'Distribütör tipi kullanıcı — yetkili distribütörünüz otomatik seçildi.'
                  : 'Boş bırakılırsa tüm distribütörler için geçerli olur.'}
              />
            </Grid>
          </Section>

          {/* #44 Oturum hesabı bağı — ekip mesajlaşmasının ÖNKOŞULU. Bağ kurulmadıkça bu personel
              organizasyon şemasında sadece bir kutudur: sisteme giren bir karşılığı olmadığı için
              ne mesaj gönderebilir ne alabilir. Bir kullanıcı yalnız BİR personele bağlanabilir;
              başka personele bağlı kullanıcılar seçenek listesine hiç gelmez. */}
          <Section icon="user" title="Oturum Hesabı" subtitle="Personelin sisteme giriş yapan kullanıcısı">
            <Grid cols={2}>
              <FieldCustom
                label="Bağlı Kullanıcı"
                hint={form.userId
                  ? 'Bu personel ekip mesajlaşmasına dahildir; yöneticisi, meslektaşları ve astlarıyla yazışabilir.'
                  : 'Bağ yok — personel sisteme giremez ve ekip mesajlaşmasına dahil değildir.'}
              >
                <UserPickerDropdown
                  value={form.userId}
                  selectedLabel={userLabel}
                  personnelId={isNew ? null : id}
                  onChange={(v, opt) => { set('userId', v); setUserLabel(opt?.label ?? ''); }}
                  showToast={showToast}
                  emptyHint="Bağlanabilir kullanıcı yok (tümü başka personele bağlı olabilir)"
                />
              </FieldCustom>
            </Grid>
          </Section>

          <Section icon="phone" title="İletişim & Adres" subtitle="Telefon, e-posta ve adres bilgileri">
            <Grid cols={2}>
              <FieldText form={form} set={set} k="phone" label="Telefon" placeholder="(0532) 222 22 22" format={formatTrPhone} />
              <FieldText form={form} set={set} k="email" label="Mail Adresi" type="email" />
              <FieldTextarea form={form} set={set} k="address" label="Adres" rows={2} span />
            </Grid>
          </Section>
        </div>
      )}

      {activeTab === 'leaves' && !isNew && (
        <div className="dps-form">
          <Section
            icon="calendar"
            title="İzin Bilgileri"
            subtitle="Personelin izin kayıtları ve vekalet bilgileri"
            right={<button className="dps-blk-add" onClick={openNewLeave}><Icon name="plus" size={15} /> İzin Ekle</button>}
          >
            {leavesLoading ? (
              <div className="dps-empty"><span>Yükleniyor…</span></div>
            ) : leaves.length === 0 ? (
              <div className="dps-empty"><Icon name="calendar" size={34} /><p>Henüz izin kaydı yok</p><span>Sağ üstten “İzin Ekle” ile ilk kaydı oluşturun.</span></div>
            ) : (
              <table className="dps-table">
                <thead>
                  <tr>
                    <th>Başlangıç</th><th>Bitiş</th><th>Talep</th><th>Onaylayan</th><th>Vekalet Eden</th><th className="dps-td-actions">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((l) => (
                    <tr key={l.id}>
                      <td><strong>{fmtDate(l.startDate)}</strong></td>
                      <td><strong>{fmtDate(l.endDate)}</strong></td>
                      <td>{fmtDate(l.requestDate)}</td>
                      <td>{l.approvedBy || '—'}</td>
                      <td>{l.substitutePersonnelId ? (personnelMap.get(l.substitutePersonnelId) || '—') : '—'}</td>
                      <td className="dps-td-actions">
                        <button className="dps-row-btn warn" onClick={() => openEditLeave(l)}>Düzenle</button>
                        <button className="dps-row-btn danger" onClick={() => handleDeleteLeave(l.id)}>Sil</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}

      {/* İzin ekle/düzenle modalı */}
      {leaveModal && (
        <SubModal
          title={leaveModal.id ? 'İzni Düzenle' : 'Yeni İzin'}
          onClose={() => setLeaveModal(null)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setLeaveModal(null)} disabled={leaveSaving}>İptal</button>
              <button className="dps-btn primary" onClick={handleSaveLeave} disabled={leaveSaving}>{leaveSaving ? 'Kaydediliyor…' : 'Kaydet'}</button>
            </>
          )}
        >
          <Grid cols={2}>
            <FieldDate form={leaveForm} set={setLeave} k="startDate" label="Başlangıç Tarihi" req />
            <FieldDate form={leaveForm} set={setLeave} k="endDate" label="Bitiş Tarihi" req />
          </Grid>
          <FieldDate form={leaveForm} set={setLeave} k="requestDate" label="Talep Tarihi" />
          <FieldText form={leaveForm} set={setLeave} k="approvedBy" label="Onaylayan Kişi" />
          <FieldCustom label="Vekalet Eden (aynı seviye)">
            <PersonnelPickerDropdown
              value={leaveForm.substitutePersonnelId}
              selectedLabel={leaveForm.substitutePersonnelId ? personnelMap.get(leaveForm.substitutePersonnelId) : ''}
              onChange={(v) => setLeave('substitutePersonnelId', v)}
              levelNo={Number(form.levelNo) || 1}
              excludeId={isNew ? undefined : id}
              placeholder="Personel seç…"
              emptyHint="Aynı seviyede başka aktif personel yok."
              showToast={showToast}
            />
          </FieldCustom>
        </SubModal>
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.firstName} {form.lastName}”</strong> personelini silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default PersonnelDetailPage;
